/* ------------------------------------------------------------------ */
/* Branded PDF generation for calculator results                       */
/* Uses dynamic import so jsPDF only loads when user clicks download    */
/* ------------------------------------------------------------------ */

export interface PdfResultRow {
  label: string
  value: string
  sub?: string
  accent?: boolean
}

export interface PdfSection {
  title: string
  rows: PdfResultRow[]
}

export interface CalculatorPdfConfig {
  title: string
  subtitle: string
  sections: PdfSection[]
  url: string
  generatedAt: string
}

type JsPDFInstance = import("jspdf").jsPDF

export async function generateCalculatorPdf(
  config: CalculatorPdfConfig,
): Promise<void> {
  const { jsPDF } = await import("jspdf")

  const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" })
  const pageWidth = 210
  const pageHeight = 297
  const marginLeft = 15
  const contentWidth = pageWidth - marginLeft * 2
  const footerHeight = 22

  /* ── Header ──────────────────────────────────────────────────────── */
  // Red brand bar
  doc.setFillColor(196, 30, 42) // #C41E2A
  doc.rect(0, 0, pageWidth, 6, "F")

  // Wordmark
  doc.setFont("helvetica", "bold")
  doc.setFontSize(18)
  doc.setTextColor(26, 26, 26) // charcoal
  doc.text("QuiteRight", marginLeft, 18)

  doc.setFont("helvetica", "normal")
  doc.setFontSize(9)
  doc.setTextColor(58, 58, 60) // slate
  doc.text("quiteright.co.uk", marginLeft, 24)

  /* ── Title ───────────────────────────────────────────────────────── */
  doc.setFont("helvetica", "bold")
  doc.setFontSize(22)
  doc.setTextColor(26, 26, 26)
  doc.text(config.title, marginLeft, 38)

  doc.setFont("helvetica", "normal")
  doc.setFontSize(10)
  doc.setTextColor(58, 58, 60)
  doc.text(config.subtitle, marginLeft, 45)

  /* ── Sections ────────────────────────────────────────────────────── */
  let y = 55

  const ensureSpace = (needed: number) => {
    if (y + needed > pageHeight - footerHeight) {
      renderFooter(doc, config, pageWidth, pageHeight)
      doc.addPage()
      y = 20
    }
  }

  for (const section of config.sections) {
    ensureSpace(20)

    // Section title
    doc.setFont("helvetica", "bold")
    doc.setFontSize(12)
    doc.setTextColor(26, 26, 26)
    doc.text(section.title, marginLeft, y)
    y += 8

    // Rows
    for (const row of section.rows) {
      const rowHeight = row.sub ? 18 : 14
      ensureSpace(rowHeight)

      if (row.accent) {
        doc.setFillColor(252, 240, 241) // very subtle red bg
        doc.roundedRect(marginLeft, y - 5, contentWidth, rowHeight, 2, 2, "F")
      }

      // Label
      doc.setFont("helvetica", "normal")
      doc.setFontSize(8)
      doc.setTextColor(128, 128, 128)
      doc.text(row.label.toUpperCase(), marginLeft + 3, y)

      // Value
      doc.setFont("helvetica", "bold")
      doc.setFontSize(row.accent ? 16 : 12)
      if (row.accent) {
        doc.setTextColor(196, 30, 42) // red
      } else {
        doc.setTextColor(26, 26, 26) // charcoal
      }
      doc.text(row.value, marginLeft + 3, y + 6)

      if (row.sub) {
        doc.setFont("helvetica", "normal")
        doc.setFontSize(7)
        doc.setTextColor(128, 128, 128)
        doc.text(row.sub, marginLeft + 3, y + 11)
        y += 18
      } else {
        y += 14
      }
    }

    y += 6
  }

  /* ── Footer ──────────────────────────────────────────────────────── */
  renderFooter(doc, config, pageWidth, pageHeight)

  /* ── Save ─────────────────────────────────────────────────────────── */
  const filename =
    config.title.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/-$/, "") +
    ".pdf"
  doc.save(filename)
}

function renderFooter(
  doc: JsPDFInstance,
  config: CalculatorPdfConfig,
  pageWidth: number,
  pageHeight: number,
) {
  doc.setFillColor(232, 228, 223) // stone
  doc.rect(0, pageHeight - 22, pageWidth, 22, "F")

  doc.setFont("helvetica", "normal")
  doc.setFontSize(7)
  doc.setTextColor(128, 128, 128)
  doc.text(`Generated on ${config.generatedAt}`, 15, pageHeight - 13)
  doc.text(config.url, 15, pageHeight - 9)
  doc.text(
    "This is an estimate for illustration only. Seek professional advice for financial decisions.",
    15,
    pageHeight - 5,
  )
}
